/**
 * The clinic as an entity, and the store as a place.
 *
 * Both are edited from two screens each - a clinic admin's and the platform's -
 * and both feed the article prompt, so the four forms have to agree on the field
 * list exactly. They are here once, with the form <-> payload conversion beside
 * them, because the conversion is where they would otherwise drift: the text
 * columns are NOT NULL and take '' for "unset", while the two URL columns are
 * nullable and must be sent as null rather than ''.
 */
import { Field } from './EditPanel.tsx';

// --- the store as a place --------------------------------------------------
export interface StoreProfileForm {
  postalCode: string;
  prefecture: string;
  city: string;
  address: string;
  accessNote: string;
  phone: string;
  hoursNote: string;
  closedNote: string;
  reservationUrl: string;
  mapUrl: string;
}

/** What the API hands back, in the shape a form can hold. */
export const storeProfileFrom = (store: {
  postal_code: string;
  prefecture: string;
  city: string;
  address: string;
  access_note: string;
  phone: string;
  hours_note: string;
  closed_note: string;
  reservation_url: string | null;
  map_url: string | null;
}): StoreProfileForm => ({
  postalCode: store.postal_code,
  prefecture: store.prefecture,
  city: store.city,
  address: store.address,
  accessNote: store.access_note,
  phone: store.phone,
  hoursNote: store.hours_note,
  closedNote: store.closed_note,
  reservationUrl: store.reservation_url ?? '',
  mapUrl: store.map_url ?? '',
});

/**
 * The form as a PATCH body.
 *
 * An emptied URL field is sent as null, not '': the column rejects anything that
 * is not https, so '' would fail validation on a field somebody was trying to
 * clear. The text fields send '' happily - that is how they are cleared.
 */
export const storeProfilePayload = (form: StoreProfileForm) => ({
  postalCode: form.postalCode.trim(),
  prefecture: form.prefecture.trim(),
  city: form.city.trim(),
  address: form.address.trim(),
  accessNote: form.accessNote,
  phone: form.phone.trim(),
  hoursNote: form.hoursNote,
  closedNote: form.closedNote,
  reservationUrl: form.reservationUrl.trim() || null,
  mapUrl: form.mapUrl.trim() || null,
});

export function StoreProfileFields({
  value,
  onChange,
  idPrefix,
}: {
  value: StoreProfileForm;
  onChange: (next: StoreProfileForm) => void;
  /** Two of these can share a page with other forms, so ids are namespaced. */
  idPrefix: string;
}) {
  const set = <K extends keyof StoreProfileForm>(key: K, next: string) =>
    onChange({ ...value, [key]: next });
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <>
      <Field label="郵便番号" htmlFor={id('postal')}>
        <input
          id={id('postal')}
          maxLength={16}
          value={value.postalCode}
          placeholder="150-0001"
          onChange={(e) => set('postalCode', e.target.value)}
        />
      </Field>
      {/*
        Registered separately from the address, and never parsed out of it.
        These two are keywords - 「渋谷区 ハイフ」 is how a local search is
        written - and 「京都府京都市」 defeats every rule for splitting an
        address string. Empty is a better answer than a confident guess.
      */}
      <Field label="都道府県" htmlFor={id('prefecture')} hint="記事の地域キーワードに使います。">
        <input
          id={id('prefecture')}
          maxLength={20}
          value={value.prefecture}
          placeholder="東京都"
          onChange={(e) => set('prefecture', e.target.value)}
        />
      </Field>
      <Field label="市区町村" htmlFor={id('city')}>
        <input
          id={id('city')}
          maxLength={60}
          value={value.city}
          placeholder="渋谷区"
          onChange={(e) => set('city', e.target.value)}
        />
      </Field>
      <Field label="住所" htmlFor={id('address')} span="wide">
        <input
          id={id('address')}
          maxLength={300}
          value={value.address}
          placeholder="東京都渋谷区神宮前1-1-1 ◯◯ビル5F"
          onChange={(e) => set('address', e.target.value)}
        />
      </Field>
      <Field
        label="アクセス"
        htmlFor={id('access')}
        span="full"
        hint="最寄駅と徒歩分数、目印など。記事のアクセス案内はここに書かれたことだけを使います。"
      >
        {/*
          A textarea, and free text in the schema too: real directions are
          「渋谷駅ハチ公口から徒歩3分／東急百貨店の隣」- two lines that do not
          decompose into a station column and a minutes column.
        */}
        <textarea
          id={id('access')}
          rows={2}
          maxLength={500}
          value={value.accessNote}
          onChange={(e) => set('accessNote', e.target.value)}
        />
      </Field>
      <Field label="電話番号" htmlFor={id('phone')}>
        <input
          id={id('phone')}
          maxLength={40}
          value={value.phone}
          placeholder="03-1234-5678"
          onChange={(e) => set('phone', e.target.value)}
        />
      </Field>
      <Field label="休診日" htmlFor={id('closed')}>
        <input
          id={id('closed')}
          maxLength={200}
          value={value.closedNote}
          placeholder="木曜・祝日"
          onChange={(e) => set('closedNote', e.target.value)}
        />
      </Field>
      <Field label="診療時間" htmlFor={id('hours')} span="full">
        <textarea
          id={id('hours')}
          rows={2}
          maxLength={500}
          value={value.hoursNote}
          placeholder="平日 11:00-20:00／土日祝 10:00-19:00（最終受付 18:00）"
          onChange={(e) => set('hoursNote', e.target.value)}
        />
      </Field>
      <Field
        label="予約ページURL"
        htmlFor={id('reservation')}
        hint="https のみ。空欄にすると記事に予約リンクは書かれません。"
      >
        <input
          id={id('reservation')}
          type="url"
          maxLength={2000}
          value={value.reservationUrl}
          placeholder="https://"
          onChange={(e) => set('reservationUrl', e.target.value)}
        />
      </Field>
      <Field label="地図URL" htmlFor={id('map')} hint="https のみ。">
        <input
          id={id('map')}
          type="url"
          maxLength={2000}
          value={value.mapUrl}
          placeholder="https://"
          onChange={(e) => set('mapUrl', e.target.value)}
        />
      </Field>
    </>
  );
}

// --- the clinic as an entity ----------------------------------------------
export interface ClinicProfileForm {
  corporateName: string;
  representative: string;
  prefecture: string;
  city: string;
  address: string;
  phone: string;
  adManagerName: string;
}

export const clinicProfileFrom = (clinic: {
  corporate_name: string;
  representative: string;
  prefecture: string;
  city: string;
  address: string;
  phone: string;
  ad_manager_name: string;
}): ClinicProfileForm => ({
  corporateName: clinic.corporate_name,
  representative: clinic.representative,
  prefecture: clinic.prefecture,
  city: clinic.city,
  address: clinic.address,
  phone: clinic.phone,
  adManagerName: clinic.ad_manager_name,
});

export const clinicProfilePayload = (form: ClinicProfileForm) => ({
  corporateName: form.corporateName.trim(),
  representative: form.representative.trim(),
  prefecture: form.prefecture.trim(),
  city: form.city.trim(),
  address: form.address.trim(),
  phone: form.phone.trim(),
  adManagerName: form.adManagerName.trim(),
});

export function ClinicProfileFields({
  value,
  onChange,
  idPrefix,
}: {
  value: ClinicProfileForm;
  onChange: (next: ClinicProfileForm) => void;
  idPrefix: string;
}) {
  const set = <K extends keyof ClinicProfileForm>(key: K, next: string) =>
    onChange({ ...value, [key]: next });
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <>
      {/* The head office's region. A store's own values win where both exist. */}
      <Field label="都道府県" htmlFor={id('prefecture')} hint="店舗に未設定のとき、記事の地域キーワードに使われます。">
        <input
          id={id('prefecture')}
          maxLength={20}
          value={value.prefecture}
          placeholder="東京都"
          onChange={(e) => set('prefecture', e.target.value)}
        />
      </Field>
      <Field label="市区町村" htmlFor={id('city')}>
        <input
          id={id('city')}
          maxLength={60}
          value={value.city}
          placeholder="千代田区"
          onChange={(e) => set('city', e.target.value)}
        />
      </Field>
      <Field
        label="運営法人名"
        htmlFor={id('corporate')}
        span="wide"
        hint="「医療法人◯◯会」など。ブランド名とは別に、記事の運営者表記に使います。"
      >
        <input
          id={id('corporate')}
          maxLength={200}
          value={value.corporateName}
          onChange={(e) => set('corporateName', e.target.value)}
        />
      </Field>
      <Field label="代表者名" htmlFor={id('representative')}>
        <input
          id={id('representative')}
          maxLength={100}
          value={value.representative}
          onChange={(e) => set('representative', e.target.value)}
        />
      </Field>
      <Field label="本社所在地" htmlFor={id('address')} span="wide">
        <input
          id={id('address')}
          maxLength={300}
          value={value.address}
          onChange={(e) => set('address', e.target.value)}
        />
      </Field>
      <Field label="代表電話" htmlFor={id('phone')}>
        <input
          id={id('phone')}
          maxLength={40}
          value={value.phone}
          onChange={(e) => set('phone', e.target.value)}
        />
      </Field>
      <Field
        label="広告管理責任者"
        htmlFor={id('admanager')}
        span="full"
        hint="医療広告ガイドラインで求められる、公開内容に責任を持つ担当者です。"
      >
        <input
          id={id('admanager')}
          maxLength={100}
          value={value.adManagerName}
          onChange={(e) => set('adManagerName', e.target.value)}
        />
      </Field>
    </>
  );
}
